/**
 * Header Component
 * Main navigation header with logo, nav links, and user menu
 * Includes GraymatterLab ecosystem navigation
 */

import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@contexts/AuthContext";
import { useTheme } from "@contexts/ThemeContext";
import { useUIStore } from "@stores/uiStore";
import { cn } from "@utils/cn";
import { STUDIO_URL } from "@/config/studio";
import logoImage from "../../assets/graymatterlab_agent_hub.png";

const navLinks = [
  { name: "Dashboard", path: "/", external: false },
  { name: "Workshops", path: "/workshops", external: false },
  { name: "Guides", path: "/guides", external: false },
  { name: "Tools", path: "/tools", external: false },
  { name: "Library", path: "/library", external: false },
  // TODO: Re-enable Templates once backend 500 errors are resolved
  // { name: "Templates", path: "/templates", external: false },
];

// Ecosystem surface URLs (env-driven per the workspace-switcher spec §7).
// Agent Studio uses STUDIO_URL from config/studio.ts (VITE_STUDIO_URL).
const AGENT_HUB_URL =
  import.meta.env.VITE_HUB_URL || "https://learn.graymatterlab.ai";
const AGENT_DESIGNER_URL =
  import.meta.env.VITE_DESIGNER_URL || "https://designer.graymatterlab.ai";

// Icon components for ecosystem products
function GraduationIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
      />
    </svg>
  );
}

function RocketIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
      />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}

// Format user role for display (e.g., "tenant_admin" -> "Tenant Admin")
function formatRole(role?: string): string {
  if (!role) return "";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const iconMap = {
  graduation: GraduationIcon,
  pencil: PencilIcon,
  rocket: RocketIcon,
};

// Shared CURRENT badge style (workspace-switcher spec §6): blue, uppercase.
const CURRENT_BADGE_CLASS =
  "text-xs px-1.5 py-0.5 rounded-full font-medium uppercase tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300";

export function Header() {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const { toggleMobileMenu, mobileMenuOpen } = useUIStore();
  const [ecosystemOpen, setEcosystemOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Ecosystem surfaces in fixed order (workspace-switcher spec §5). This app is
  // Agent Hub, so the Hub row is the non-interactive "current" entry; the other
  // two are external links.
  const ecosystemProducts = useMemo(
    () => [
      {
        name: "Agent Studio",
        description: "Build & operate agents",
        href: STUDIO_URL,
        icon: "rocket" as const,
        current: false,
      },
      {
        name: "Agent Hub",
        description: "Learn & train",
        href: AGENT_HUB_URL,
        icon: "graduation" as const,
        current: true,
      },
      {
        name: "Agent Designer",
        description: "Design agents visually",
        href: AGENT_DESIGNER_URL,
        icon: "pencil" as const,
        current: false,
      },
    ],
    [],
  );

  return (
    <header className="sticky top-0 z-40 w-full border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src={logoImage}
                alt="GraymatterLab Agent Hub"
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Right side - Navigation + Controls */}
          <div className="flex items-center">
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1 mr-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    "px-3 py-2 text-sm font-medium transition-colors relative",
                    location.pathname === link.path
                      ? "text-gray-900 dark:text-white after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-primary-500"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
                  )}
                >
                  {link.name}
                </Link>
              ))}

              {/* Ecosystem Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setEcosystemOpen(!ecosystemOpen)}
                  onBlur={() => setTimeout(() => setEcosystemOpen(false), 150)}
                  className={cn(
                    "px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1 relative",
                    ecosystemOpen
                      ? "text-gray-900 dark:text-white after:absolute after:bottom-0 after:left-3 after:right-3 after:h-0.5 after:bg-primary-500"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
                  )}
                >
                  Workspaces
                  <svg
                    className={cn(
                      "w-4 h-4 transition-transform",
                      ecosystemOpen && "rotate-180",
                    )}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {ecosystemOpen && (
                  <div className="absolute right-0 mt-2 w-72 rounded-xl bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 p-2 z-50">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 mb-2">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        GraymatterLab Ecosystem
                      </p>
                    </div>
                    {ecosystemProducts.map((product) => {
                      const IconComponent =
                        iconMap[product.icon as keyof typeof iconMap];
                      const content = (
                        <div
                          className={cn(
                            "flex items-start gap-3 p-3 rounded-lg transition-colors",
                            product.current
                              ? "bg-gray-50 dark:bg-gray-700/50"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700/50",
                          )}
                        >
                          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {product.name}
                              </span>
                              {product.current ? (
                                <span className={CURRENT_BADGE_CLASS}>
                                  Current
                                </span>
                              ) : (
                                <ExternalLinkIcon className="w-3 h-3 text-gray-400" />
                              )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              {product.description}
                            </p>
                          </div>
                        </div>
                      );

                      return product.current ? (
                        <div key={product.name} aria-current="page">
                          {content}
                        </div>
                      ) : (
                        <a
                          key={product.name}
                          href={product.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block"
                        >
                          {content}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </nav>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors-fast"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>

              {/* User menu */}
              {isAuthenticated ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
                    className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors-fast"
                  >
                    <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center shadow-sm">
                      <span className="text-sm font-medium text-white">
                        {user?.full_name?.[0] ||
                          user?.email?.[0]?.toUpperCase() ||
                          "U"}
                      </span>
                    </div>
                    <div className="hidden sm:flex flex-col items-start">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                        {user?.full_name?.split(" ")[0] ||
                          user?.email?.split("@")[0]}
                      </span>
                      {user?.role && (
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          {formatRole(user.role)}
                        </span>
                      )}
                    </div>
                    <svg
                      className={cn(
                        "hidden sm:block w-4 h-4 text-gray-400 transition-transform duration-200",
                        userMenuOpen && "rotate-180",
                      )}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 rounded-xl bg-white dark:bg-gray-800 shadow-xl ring-1 ring-black/5 dark:ring-white/10 py-2 z-50 transform origin-top-right transition-all">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {user?.full_name || user?.email?.split("@")[0]}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {user?.email}
                        </p>
                      </div>

                      <div className="py-1">
                        <Link
                          to="/profile/settings"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <svg
                            className="w-4 h-4 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          Profile Settings
                        </Link>

                        {["tenant_admin", "super_admin"].includes(
                          user?.role || "",
                        ) && (
                          <>
                            <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 mt-1">
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                Admin
                              </p>
                            </div>
                            {/* Organization Admin - available to tenant_admin and super_admin */}
                            <Link
                              to="/org-admin"
                              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                              onClick={() => setUserMenuOpen(false)}
                            >
                              <svg
                                className="w-4 h-4 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                />
                              </svg>
                              Organization Admin
                            </Link>
                            {/* Platform Admin - super_admin only */}
                            {user?.role === "super_admin" && (
                              <Link
                                to="/admin"
                                className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                onClick={() => setUserMenuOpen(false)}
                              >
                                <svg
                                  className="w-4 h-4 text-gray-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                                Platform Admin
                              </Link>
                            )}
                          </>
                        )}
                      </div>

                      <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          logout();
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors-fast"
                >
                  Sign In
                </Link>
              )}

              {/* Mobile menu button */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Toggle menu"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-4">
            <nav className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={toggleMobileMenu}
                  className={cn(
                    "px-3 py-2 text-base font-medium border-l-2 transition-colors",
                    location.pathname === link.path
                      ? "border-primary-500 text-gray-900 dark:text-white bg-primary-50 dark:bg-primary-900/20"
                      : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800",
                  )}
                >
                  {link.name}
                </Link>
              ))}

              {/* Ecosystem Section */}
              <div className="pt-4 mt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  GraymatterLab Ecosystem
                </p>
                {ecosystemProducts.map((product) => {
                  const IconComponent =
                    iconMap[product.icon as keyof typeof iconMap];
                  const content = (
                    <div className="flex items-center gap-3 px-3 py-2">
                      <IconComponent className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      <span className="flex-1 text-base font-medium text-gray-600 dark:text-gray-300">
                        {product.name}
                      </span>
                      {product.current ? (
                        <span className={CURRENT_BADGE_CLASS}>Current</span>
                      ) : (
                        <ExternalLinkIcon className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  );

                  return product.current ? (
                    <div
                      key={product.name}
                      aria-current="page"
                      className="block rounded-md bg-gray-100 dark:bg-gray-800"
                    >
                      {content}
                    </div>
                  ) : (
                    <a
                      key={product.name}
                      href={product.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={toggleMobileMenu}
                      className="block rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      {content}
                    </a>
                  );
                })}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
